import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { X } from 'lucide-react';
import axios from 'axios';
import ImageUploader from './ImageUploader';
import TechnicalDocumentsManager from './TechnicalDocumentsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectForm = ({ project, onClose }) => {
  const createEmptyDocuments = () => ({
    arquitetonico: Array(5).fill(null).map(() => ({ url: '', name: '' })),
    interiores: Array(5).fill(null).map(() => ({ url: '', name: '' }))
  });

  const [formData, setFormData] = useState({
    name: '',
    images: [],
    discount: 35,
    showDiscount: true,
    tags: [],
    terrain: '',
    area: '',
    bedrooms: 1,
    bathrooms: 1,
    description: '',
    price: 0,
    interiorPrice: 1618.50,
    rrtPrice: 1300.00,
    showInterior: true,
    showRRT: true,
    shares: 0,
    // Technical specifications
    terrainWidth: '',
    terrainLength: '',
    terrainType: 'Plano',
    houseWidth: '',
    houseLength: '',
    facadeType: '',
    garageSpaces: 0,
    // Technical documents
    technicalDocuments: createEmptyDocuments()
  });
  const availableTags = ['Chalé', 'Casa de Campo', 'Casa de Praia', 'Casa Modular', 'Térrea', 'Sobrado'];
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData(project);
    }
  }, [project]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate images
    if (formData.images.length === 0) {
      alert('Por favor, adicione pelo menos uma imagem do projeto');
      return;
    }
    
    // Validate tags
    if (formData.tags.length === 0) {
      alert('Por favor, selecione pelo menos uma tag (tipo de construção)');
      return;
    }
    
    setLoading(true);

    try {
      if (project) {
        // Update
        await axios.put(
          `${BACKEND_URL}/api/admin/projects/${project.id}`,
          formData,
          getAuthHeaders()
        );
      } else {
        // Create
        await axios.post(
          `${BACKEND_URL}/api/admin/projects`,
          formData,
          getAuthHeaders()
        );
      }

      onClose();
    } catch (error) {
      alert('Erro ao salvar projeto: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {project ? 'Editar Projeto' : 'Novo Projeto'}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="hover:bg-gray-100"
            >
              <X size={24} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Uploader */}
            <ImageUploader
              images={formData.images}
              onChange={(images) => setFormData(prev => ({ ...prev, images }))}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome do Projeto *
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Projeto Minnesota"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Desconto (%)
                </label>
                <div className="space-y-3">
                  <Input
                    name="discount"
                    type="number"
                    value={formData.discount}
                    onChange={handleChange}
                    min="0"
                    max="100"
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showDiscount"
                      checked={formData.showDiscount}
                      onChange={(e) => setFormData(prev => ({ ...prev, showDiscount: e.target.checked }))}
                      className="w-4 h-4 text-[#00A86B] border-gray-300 rounded focus:ring-[#00A86B]"
                    />
                    <label htmlFor="showDiscount" className="text-sm text-gray-700">
                      Exibir badge de desconto
                    </label>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Tipo de Construção (Tags) *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {availableTags.map(tag => (
                    <div
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`
                        flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all
                        ${formData.tags.includes(tag)
                          ? 'border-[#00A86B] bg-[#00A86B] bg-opacity-10 text-[#00A86B] font-semibold'
                          : 'border-gray-300 hover:border-[#00A86B] hover:bg-gray-50'
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={formData.tags.includes(tag)}
                        onChange={() => {}}
                        className="mr-2 w-4 h-4 text-[#00A86B] border-gray-300 rounded focus:ring-[#00A86B]"
                      />
                      <span className="text-sm">{tag}</span>
                    </div>
                  ))}
                </div>
                {formData.tags.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    Selecionado(s): {formData.tags.join(', ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Terreno *
                </label>
                <Input
                  name="terrain"
                  value={formData.terrain}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 36.12x31.31"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Área (m²) *
                </label>
                <Input
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 250"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Quartos *
                </label>
                <Input
                  name="bedrooms"
                  type="number"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Banheiros *
                </label>
                <Input
                  name="bathrooms"
                  type="number"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Preço (R$) *
                </label>
                <Input
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Compartilhamentos
                </label>
                <Input
                  name="shares"
                  type="number"
                  value={formData.shares}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>

            {/* Projetos Complementares Section */}
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Projetos Complementares</h3>
              
              {/* Interiores */}
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-800">
                    Interiores
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showInterior"
                      checked={formData.showInterior}
                      onChange={(e) => setFormData(prev => ({ ...prev, showInterior: e.target.checked }))}
                      className="w-4 h-4 text-[#00A86B] border-gray-300 rounded focus:ring-[#00A86B]"
                    />
                    <label htmlFor="showInterior" className="text-sm text-gray-700">
                      Exibir esta opção
                    </label>
                  </div>
                </div>
                {formData.showInterior && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      Preço (R$) *
                    </label>
                    <Input
                      name="interiorPrice"
                      type="number"
                      step="0.01"
                      value={formData.interiorPrice}
                      onChange={handleChange}
                      required={formData.showInterior}
                      min="0"
                    />
                  </div>
                )}
              </div>

              {/* RRT */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-800">
                    RRT + Assessoria Técnica
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showRRT"
                      checked={formData.showRRT}
                      onChange={(e) => setFormData(prev => ({ ...prev, showRRT: e.target.checked }))}
                      className="w-4 h-4 text-[#00A86B] border-gray-300 rounded focus:ring-[#00A86B]"
                    />
                    <label htmlFor="showRRT" className="text-sm text-gray-700">
                      Exibir esta opção
                    </label>
                  </div>
                </div>
                {formData.showRRT && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      Preço (R$) *
                    </label>
                    <Input
                      name="rrtPrice"
                      type="number"
                      step="0.01"
                      value={formData.rrtPrice}
                      onChange={handleChange}
                      required={formData.showRRT}
                      min="0"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descrição *
              </label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                placeholder="Descreva o projeto..."
              />
            </div>

            {/* Ficha Técnica Section */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Ficha Técnica - Informações do Projeto</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Terreno
                  </label>
                  <select
                    name="terrainType"
                    value={formData.terrainType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A86B]"
                  >
                    <option value="Plano">Plano</option>
                    <option value="Aclive">Aclive</option>
                    <option value="Declive">Declive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Fachada
                  </label>
                  <Input
                    name="facadeType"
                    value={formData.facadeType}
                    onChange={handleChange}
                    placeholder="Ex: Moderna, Clássica, Contemporânea"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Largura da Casa (m)
                  </label>
                  <Input
                    name="houseWidth"
                    value={formData.houseWidth}
                    onChange={handleChange}
                    placeholder="Ex: 20,35"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Comprimento da Casa (m)
                  </label>
                  <Input
                    name="houseLength"
                    value={formData.houseLength}
                    onChange={handleChange}
                    placeholder="Ex: 15,3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Garagem (vagas)
                  </label>
                  <Input
                    name="garageSpaces"
                    type="number"
                    value={formData.garageSpaces}
                    onChange={handleChange}
                    min="0"
                    placeholder="Ex: 3"
                  />
                </div>
              </div>
            </div>

            {/* Technical Documents Section */}
            <TechnicalDocumentsManager
              documents={formData.technicalDocuments}
              onChange={(docs) => setFormData(prev => ({ ...prev, technicalDocuments: docs }))}
            />

            <div className="flex space-x-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
              >
                {loading ? 'Salvando...' : (project ? 'Atualizar Projeto' : 'Criar Projeto')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="px-8"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ProjectForm;
